import { ProductCategoriesLink } from "components/Navbar/types"
import { useAppDispatch } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { LanguageService } from "services/languageService"
import { clearBillingState } from "store/billing/billingSlice"
import { setTypes } from "store/product/productSlice"
import { clearUserInfo, setUserInfo } from "store/user/userSlice"
import { IntegrationType, UserStatusTypes } from "types"
import { clearTiendanubeToken, setTiendanubeToken } from "utils/authSessionStorage"
import { normalizeMerchantStatus } from "utils/userStatus"
import { Card, CardContent } from "@/components/ui/card"
import { Loader2 } from "lucide-react"

export const LoginTiendanubePage = () => {
  const { t, i18n } = useTranslation()
  const service = getTintVTOService()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams, _] = useSearchParams()
  const [code] = useState(searchParams.get("code"))

  const [error, setError] = useState("")
  const [requestSended, setRequestSended] = useState(false)
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false)

  useEffect(() => {
    dispatch(clearBillingState())
    dispatch(clearUserInfo())
    clearTiendanubeToken()
    service.changeAwsTokenAndUserType(null, IntegrationType.COMMON)
    if (!code) {
      setError(t("loginTiendanubePage.missingCode"))
      return
    }

    if (requestSended || hasAttemptedLogin) return

    const loginByCode = async (authCode: string) => {
      setHasAttemptedLogin(true)
      setRequestSended(true)
      const response = await service.loginTiendanube(authCode)

      if (typeof response === "boolean" && !response) {
        setError(t("loginTiendanubePage.invalidCode"))
      } else {
        const userInfo = {
          integrationType: IntegrationType.TIENDANUBE,
          merchantId: response.data.merchantId,
          username: response.data.username,
          displayName: response.data.username,
          status: normalizeMerchantStatus(response.data.status ?? UserStatusTypes.NEW),
          groups: response.data.groups,
          userExpired: false,
        }

        dispatch(setUserInfo({ userInfo }))
        setTiendanubeToken(response.token)
        service.changeAwsTokenAndUserType(response.token, IntegrationType.TIENDANUBE)

        const { data } = await service.types()
        const types = data?.types ?? []

        dispatch(setTypes(types))

        // Set language based on user choice or default AFTER types are loaded
        if (!LanguageService.hasUserMadeChoice()) {
          const defaultLanguage = LanguageService.getDefaultLanguage(userInfo.integrationType)
          LanguageService.saveLanguage(defaultLanguage)
          i18n.changeLanguage(defaultLanguage)
        }

        navigate(ProductCategoriesLink.MAKEUP)
      }
      setRequestSended(false)
    }

    void loginByCode(code)
  }, [code, t, requestSended, hasAttemptedLogin, dispatch, service, i18n, navigate])

  return (
    <div
      className="flex h-full items-center justify-center flex-col bg-muted"
      data-testid="login-tiendanube-page"
    >
      <Card className="w-[30rem] rounded-2xl" data-testid="login-tiendanube-card">
        <CardContent className="flex flex-col items-center px-6 pt-6 pb-2">
          <div className="flex flex-col items-center justify-center gap-4">
            <img
              src="../tint-logo.svg"
              alt={t("loginTiendanubePage.logoAlt")}
              data-testid="login-tiendanube-logo"
              width="64px"
              height="64px"
            />
            <h1 className="text-2xl font-bold text-center">
              {t("loginTiendanubePage.redirectMessage")}
            </h1>
          </div>
          {requestSended ? (
            <div className="h-[68px] flex items-center justify-end">
              <Loader2
                className="h-8 w-8 animate-spin text-muted-foreground"
                data-testid="login-tiendanube-loading"
              />
            </div>
          ) : (
            <div className="py-6">
              {error && (
                <p className="text-sm text-destructive" data-testid="login-tiendanube-error">
                  {error}
                </p>
              )}
            </div>
          )}

          <div className="py-2 pt-4">
            <a
              href="https://www.banuba.com/es/support-tint"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline hover:text-primary/80"
              data-testid="login-tiendanube-support"
            >
              {t("loginTiendanubePage.supportLink")}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
